import React, { useEffect, useState } from "react";
import { getUser } from "../api/auth";
import { getUserCredits } from "../api/businesses";
import { getStripeUrlForPurchasingPhotoCredits } from "../api/stripe";

const SettingsPage: React.FC = () => {
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    credits: 0,
  });
  const [creditsToBuy, setCreditsToBuy] = useState("1"); // Initialize as string to handle empty input
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const creditPrice = 3.99;
  const totalCost = parseInt(creditsToBuy || "1", 10) * creditPrice;

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const res = await getUser();
        const response = res.data;

        // Fetch user credits
        const creditRes = await getUserCredits();
        const credits = creditRes.data.credits;
        setUserInfo({
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email,
          credits: credits,
        });
        setLoading(false);
      } catch (err) {
        setError("Failed to load user information.");
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  const handleCreditsToBuyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setCreditsToBuy(value);
    }
  };

  const handleBuyCredits = async () => {
    try {
      const creditsAmount = parseInt(creditsToBuy, 10) || 1; // Default to 1 if empty
      const res = await getStripeUrlForPurchasingPhotoCredits(creditsAmount);
      const checkoutUrl = res.data.url;

      // Redirect the user to the Stripe checkout URL
      window.location.href = checkoutUrl;
    } catch (err) {
      setError("Failed to initiate purchase. Please try again.");
      console.error("Error during purchase:", err);
    }
  };

  if (loading) {
    return <div className="text-center p-6">Loading...</div>;
  }

  if (error) {
    return <div className="text-center p-6 text-red-500">{error}</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto bg-gray-50 min-h-screen overflow-auto">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
        Settings
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Personal Information Section */}
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border flex flex-col">
          <h3 className="text-xl md:text-2xl font-semibold mb-4">
            Personal Information
          </h3>
          <p className="mb-4">
            <strong>Email:</strong> {userInfo.email}
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              type="text"
              value={userInfo.firstName}
              readOnly
              className="w-full mt-1 p-2 md:p-3 border border-gray-300 rounded-lg bg-gray-100 text-sm md:text-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              type="text"
              value={userInfo.lastName}
              readOnly
              className="w-full mt-1 p-2 md:p-3 border border-gray-300 rounded-lg bg-gray-100 text-sm md:text-lg"
            />
          </div>
        </div>

        {/* Buy Credits Section */}
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg border flex flex-col">
          <h3 className="text-xl md:text-2xl font-semibold mb-4">
            Buy More Photo Credits
          </h3>
          <p className="mb-4">
            <strong>Remaining Credits:</strong> {userInfo.credits}
          </p>
          <p className="text-sm md:text-base text-gray-500 mb-4">
            Price: $3.99 per photo
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Credits to Buy
            </label>
            <input
              type="number"
              min="1"
              value={creditsToBuy}
              onChange={handleCreditsToBuyChange}
              className="w-full mt-1 p-2 md:p-3 border border-gray-300 rounded-lg bg-white text-sm md:text-lg"
              placeholder="1"
            />
          </div>

          <p className="mb-4 text-gray-700">
            Total Cost: <strong>${totalCost.toFixed(2)}</strong>
          </p>

          <button
            onClick={handleBuyCredits}
            className="w-full py-2 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 text-sm md:text-lg"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
